import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogFormGrid, DialogFormSection,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { eduAccreditationAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { Award, Plus, Pencil, Trash2, Gauge, Loader2, ListChecks, Download, ArrowLeft, CheckCircle2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const BODIES = ['NAAC', 'NBA', 'AICTE', 'UGC', 'NIRF', 'ISO'];
const STATUSES = ['in_progress', 'submitted', 'accredited', 'expired'];
const STATUS_LABEL = {
  in_progress: 'In Progress', submitted: 'Submitted', accredited: 'Accredited', expired: 'Expired',
};
const SOURCES = ['manual', 'auto'];

const EMPTY_FRAMEWORK = { body: 'NAAC', cycle: '', status: 'in_progress', visit_date: '' };
const EMPTY_METRIC = { criterion: '', metric_key: '', target: 0, actual: 0, weight: 1, source: 'manual' };

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || [];
};

const EducationAccreditationManagement = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [loading, setLoading] = useState(false);
  const [frameworks, setFrameworks] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FRAMEWORK);

  // Selected framework → metrics + readiness view
  const [selected, setSelected] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [readiness, setReadiness] = useState(null);

  const [metricDialogOpen, setMetricDialogOpen] = useState(false);
  const [editingMetricId, setEditingMetricId] = useState(null);
  const [metricForm, setMetricForm] = useState(EMPTY_METRIC);

  const loadFrameworks = async () => {
    setLoading(true);
    try {
      const res = await eduAccreditationAPI.listFrameworks();
      setFrameworks(toArray(res));
    } catch (err) {
      if (err?.response?.status === 402) {
        setFrameworks([]);
      } else {
        Swal.fire('Could not load frameworks', err?.response?.data?.detail || 'Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFrameworks(); }, []);

  const stats = useMemo(() => {
    const total = frameworks.length;
    const inProgress = frameworks.filter((f) => f.status === 'in_progress').length;
    const accredited = frameworks.filter((f) => f.status === 'accredited').length;
    const bodies = new Set(frameworks.map((f) => f.body)).size;
    return { total, inProgress, accredited, bodies };
  }, [frameworks]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setMetricField = (key, value) => setMetricForm((prev) => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FRAMEWORK); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({ ...EMPTY_FRAMEWORK, ...row, visit_date: row.visit_date || '' });
    setDialogOpen(true);
  };

  const submitFramework = async () => {
    if (!form.cycle?.trim()) return Swal.fire('Cycle required', 'Enter the accreditation cycle (e.g. Cycle 3).', 'warning');
    const payload = {
      body: form.body,
      cycle: form.cycle.trim(),
      status: form.status,
      visit_date: form.visit_date || null,
    };
    try {
      if (editingId) {
        await eduAccreditationAPI.updateFramework(editingId, payload);
      } else {
        await eduAccreditationAPI.createFramework(payload);
      }
      setDialogOpen(false);
      await loadFrameworks();
      Swal.fire({ icon: 'success', title: editingId ? 'Framework updated' : 'Framework added', timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please review the form and try again.', 'error');
    }
  };

  const removeFramework = async (row) => {
    const ok = await Swal.fire({
      icon: 'warning', title: 'Delete framework?',
      text: `${row.body} · ${row.cycle} and all its metrics will be removed.`,
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    try {
      await eduAccreditationAPI.deleteFramework(row.id);
      if (selected?.id === row.id) closeFramework();
      await loadFrameworks();
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Delete failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const openFramework = async (row) => {
    setSelected(row);
    setReadiness(null);
    await Promise.all([loadMetrics(row.id), loadReadiness(row.id)]);
  };
  const closeFramework = () => { setSelected(null); setMetrics([]); setReadiness(null); };

  const loadMetrics = async (frameworkId) => {
    setMetricsLoading(true);
    try {
      const res = await eduAccreditationAPI.listMetrics(frameworkId);
      setMetrics(toArray(res));
    } catch (err) {
      Swal.fire('Could not load metrics', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadReadiness = async (frameworkId) => {
    try {
      const res = await eduAccreditationAPI.readiness(frameworkId);
      setReadiness(res.data);
    } catch (err) {
      setReadiness(null);
    }
  };

  const refreshSelected = async () => {
    if (!selected) return;
    await Promise.all([loadMetrics(selected.id), loadReadiness(selected.id)]);
  };

  const openCreateMetric = () => { setEditingMetricId(null); setMetricForm(EMPTY_METRIC); setMetricDialogOpen(true); };
  const openEditMetric = (row) => {
    setEditingMetricId(row.id);
    setMetricForm({ ...EMPTY_METRIC, ...row });
    setMetricDialogOpen(true);
  };

  const submitMetric = async () => {
    if (!selected) return;
    if (!metricForm.criterion?.trim()) return Swal.fire('Criterion required', 'Enter the criterion name.', 'warning');
    if (!metricForm.metric_key?.trim()) return Swal.fire('Metric key required', 'Enter the metric key (e.g. 1.1.1).', 'warning');
    const payload = {
      criterion: metricForm.criterion.trim(),
      metric_key: metricForm.metric_key.trim(),
      target: Number(metricForm.target) || 0,
      actual: Number(metricForm.actual) || 0,
      weight: Number(metricForm.weight) || 0,
      source: metricForm.source,
    };
    try {
      if (editingMetricId) {
        await eduAccreditationAPI.updateMetric(editingMetricId, payload);
      } else {
        await eduAccreditationAPI.createMetric(selected.id, payload);
      }
      setMetricDialogOpen(false);
      await refreshSelected();
      Swal.fire({ icon: 'success', title: editingMetricId ? 'Metric updated' : 'Metric added', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please review the form and try again.', 'error');
    }
  };

  const removeMetric = async (row) => {
    const ok = await Swal.fire({
      icon: 'warning', title: 'Delete metric?',
      text: `${row.criterion} · ${row.metric_key} will be removed.`,
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    try {
      await eduAccreditationAPI.deleteMetric(row.id);
      await refreshSelected();
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Delete failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const exportReadiness = async () => {
    if (!selected) return;
    try {
      const res = await eduAccreditationAPI.export(selected.id);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `accreditation_${selected.body}_${selected.cycle}_readiness.json`.replace(/\s+/g, '_');
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Swal.fire('Export failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  // -------------------------- framework detail view --------------------------
  if (selected) {
    const overall = readiness?.overall_score ?? 0;
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Button variant="ghost" size="sm" onClick={closeFramework} className="mb-1 -ml-2">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to frameworks
            </Button>
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Award className="h-6 w-6 text-indigo-600" /> {selected.body} · {selected.cycle}
            </h2>
            <p className="text-sm text-muted-foreground">
              <Badge variant="secondary">{STATUS_LABEL[selected.status] || selected.status}</Badge>
              {selected.visit_date ? ` · Visit: ${selected.visit_date}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={exportReadiness}>
              <Download className="h-4 w-4 mr-1" /> Export
            </Button>
            {canManage && (
              <Button onClick={openCreateMetric}>
                <Plus className="h-4 w-4 mr-1" /> Add Metric
              </Button>
            )}
          </div>
        </div>

        {/* Readiness meter */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Gauge className="h-5 w-5 text-indigo-600" /> Readiness Score</CardTitle>
            <CardDescription>Weighted readiness across all recorded metrics.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-end justify-between">
              <div className="text-4xl font-semibold">{overall}%</div>
              <div className="text-sm text-muted-foreground">{metrics.length} metric{metrics.length === 1 ? '' : 's'}</div>
            </div>
            <Progress value={overall} className="h-3" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Overall Readiness" value={`${overall}%`} icon={Gauge} />
              <StatCard label="Metrics Tracked" value={metrics.length} icon={ListChecks} />
              <StatCard
                label="Fully Met"
                value={(readiness?.criteria || []).filter((c) => c.pct >= 1).length}
                icon={CheckCircle2}
              />
              <StatCard label="Body" value={selected.body} icon={Award} />
            </div>
          </CardContent>
        </Card>

        {/* Metrics table */}
        <Card>
          <CardHeader>
            <CardTitle>Metrics</CardTitle>
            <CardDescription>Criteria and their target vs. actual attainment.</CardDescription>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading metrics…
              </div>
            ) : metrics.length === 0 ? (
              <EmptyState
                icon={ListChecks}
                title="No metrics yet"
                description={canManage ? 'Add a metric to start tracking readiness.' : 'No metrics have been added yet.'}
              />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Criterion</TableHead>
                      <TableHead>Metric</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Actual</TableHead>
                      <TableHead>Weight</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead className="w-40">Attainment</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {metrics.map((row) => {
                      const target = Number(row.target) || 0;
                      const actual = Number(row.actual) || 0;
                      const pct = target > 0 ? Math.min(actual / target, 1) : 0;
                      const pctInt = Math.round(pct * 100);
                      return (
                        <TableRow key={row.id}>
                          <TableCell className="font-medium">{row.criterion}</TableCell>
                          <TableCell>{row.metric_key}</TableCell>
                          <TableCell>{target}</TableCell>
                          <TableCell>{actual}</TableCell>
                          <TableCell>{row.weight}</TableCell>
                          <TableCell><Badge variant="outline">{row.source}</Badge></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={pctInt} className="h-2 flex-1" />
                              <span className="text-xs text-muted-foreground w-9 text-right">{pctInt}%</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {canManage && <Button variant="ghost" size="icon" onClick={() => openEditMetric(row)} title="Edit"><Pencil className="h-4 w-4" /></Button>}
                            {canManage && <Button variant="ghost" size="icon" onClick={() => removeMetric(row)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Metric create / edit dialog */}
        <Dialog open={metricDialogOpen} onOpenChange={setMetricDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{editingMetricId ? 'Edit Metric' : 'Add Metric'}</DialogTitle>
            </DialogHeader>
            <DialogFormSection>
              <DialogFormGrid>
                <div className="space-y-1">
                  <Label>Criterion *</Label>
                  <Input value={metricForm.criterion} onChange={(e) => setMetricField('criterion', e.target.value)} placeholder="Curricular Aspects" />
                </div>
                <div className="space-y-1">
                  <Label>Metric Key *</Label>
                  <Input value={metricForm.metric_key} onChange={(e) => setMetricField('metric_key', e.target.value)} placeholder="1.1.1" />
                </div>
                <div className="space-y-1">
                  <Label>Target</Label>
                  <Input type="number" value={metricForm.target} onChange={(e) => setMetricField('target', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Actual</Label>
                  <Input type="number" value={metricForm.actual} onChange={(e) => setMetricField('actual', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Weight</Label>
                  <Input type="number" value={metricForm.weight} onChange={(e) => setMetricField('weight', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Source</Label>
                  <Select value={metricForm.source} onValueChange={(v) => setMetricField('source', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </DialogFormGrid>
            </DialogFormSection>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMetricDialogOpen(false)}>Cancel</Button>
              <Button onClick={submitMetric}>{editingMetricId ? 'Save changes' : 'Add metric'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // -------------------------- frameworks list view --------------------------
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Award className="h-6 w-6 text-indigo-600" /> Accreditation Support
          </h2>
          <p className="text-sm text-muted-foreground">
            Track accreditation frameworks (NAAC, NBA, AICTE, UGC, NIRF, ISO) and their readiness against target metrics.
          </p>
        </div>
        {canManage && (
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> Add Framework
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Frameworks" value={stats.total} icon={Award} />
        <StatCard label="In Progress" value={stats.inProgress} icon={Gauge} />
        <StatCard label="Accredited" value={stats.accredited} icon={CheckCircle2} />
        <StatCard label="Bodies Covered" value={stats.bodies} icon={ListChecks} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Accreditation Frameworks</CardTitle>
          <CardDescription>Open a framework to manage metrics and view readiness.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading frameworks…
            </div>
          ) : frameworks.length === 0 ? (
            <EmptyState
              icon={Award}
              title="No accreditation frameworks yet"
              description={canManage ? 'Add a framework to begin tracking accreditation readiness.' : 'No frameworks have been added yet.'}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Body</TableHead>
                    <TableHead>Cycle</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Visit Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {frameworks.map((row) => (
                    <TableRow key={row.id} className="cursor-pointer" onClick={() => openFramework(row)}>
                      <TableCell className="font-medium">{row.body}</TableCell>
                      <TableCell>{row.cycle}</TableCell>
                      <TableCell><Badge variant="secondary">{STATUS_LABEL[row.status] || row.status}</Badge></TableCell>
                      <TableCell>{row.visit_date || '—'}</TableCell>
                      <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" onClick={() => openFramework(row)} title="Open"><Gauge className="h-4 w-4" /></Button>
                        {canManage && <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit"><Pencil className="h-4 w-4" /></Button>}
                        {canManage && <Button variant="ghost" size="icon" onClick={() => removeFramework(row)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Framework create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Framework' : 'Add Framework'}</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <DialogFormGrid>
              <div className="space-y-1">
                <Label>Accreditation Body *</Label>
                <Select value={form.body} onValueChange={(v) => setField('body', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BODIES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Cycle *</Label>
                <Input value={form.cycle} onChange={(e) => setField('cycle', e.target.value)} placeholder="Cycle 3" />
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setField('status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Visit Date</Label>
                <Input type="date" value={form.visit_date || ''} onChange={(e) => setField('visit_date', e.target.value)} />
              </div>
            </DialogFormGrid>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitFramework}>{editingId ? 'Save changes' : 'Add framework'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducationAccreditationManagement;
